import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { PURCHASE_REQUEST_STATUSES } from '../../constants/PurchaseRequestStatuses';
import { purchaseRequestStatusChangeForSupplier } from '../../thunks/purchase_requests_thunks';
import { createMultipleEpslAsDeletedForSupplier } from '../../thunks/equipment_per_stock_locations_thunks';
import { createMultipleAssetsAsDeletedForSupplier } from '../../thunks/assets_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const PR_ACTIONS = {
	ORDER: 'ordered',
	FULFILL: 'fulfilled',
};

const usePurchaseRequestFulfill = (purchaseRequest, onSuccess) => {
	const dispatch: any = useDispatch();

	const [currentAction, setCurrentAction] = useState(null);
	const [equipmentSerialNumberVisible, setEquipmentSerialNumberVisible] = useState(false);
	const [createdEntities, setCreatedEntities] = useState([]);
	const [receivingEntityType, setReceivingEntityType] = useState(null);

	const prId = purchaseRequest && purchaseRequest.id;

	const isLocationPR = useMemo(() => nullSafeGet('locationId', purchaseRequest), [purchaseRequest]);

	const equipmentLineItems = useMemo(
		() => nullSafeGetOrElse('equipmentSupplierPurchaseRequestLineItems', purchaseRequest, []),
		[purchaseRequest]
	);

	const markFulfilledDisabledWithAssetTypeId = useMemo(() => {
		return isLocationPR && nullSafeGet('0', equipmentLineItems)
			? !!equipmentLineItems.find((_) => !nullSafeGet('equipmentType.assetTypeId', _))
			: false;
	}, [equipmentLineItems, isLocationPR]);

	const updatePurchaseRequestStatus = useCallback(
		(id, status, equipmentReceiptValues = undefined) =>
			dispatch(purchaseRequestStatusChangeForSupplier(id, status, equipmentReceiptValues)),
		[dispatch]
	);

	const createDeletedAssetsBulk = useCallback(() => {
		const allAssetEntities = equipmentLineItems.flatMap((lineItem) =>
			Array.from(Array(lineItem.equipmentQuantity).keys()).map((_) => ({
				locationId: nullSafeGet('locationId', purchaseRequest),
				name: nullSafeGetOrElse('equipmentType.modelName', lineItem, ''),
				isActive: true,
				images: [],
				...(nullSafeGet('equipmentType.assetTypeId', lineItem) && {
					assetTypeId: nullSafeGet('equipmentType.assetTypeId', lineItem),
				}),
				assetTypeDetails: [],
				isLeased: false,
				buyerCompanyId: nullSafeGet('location.buyerCompanyId', purchaseRequest),
			}))
		);

		return dispatch(createMultipleAssetsAsDeletedForSupplier(allAssetEntities));
	}, [dispatch, equipmentLineItems, purchaseRequest]);

	const createDeletedEpslsBulk = useCallback(() => {
		const allEpslEntities = equipmentLineItems.flatMap((lineItem) =>
			Array.from(Array(lineItem.equipmentQuantity).keys()).map((_) => ({
				stockLocationId: nullSafeGet('stockLocationId', purchaseRequest),
				equipmentTypeId: lineItem.equipmentTypeId,
				name: nullSafeGetOrElse('equipmentType.modelName', lineItem, ''),
				isRental: false,
				vendorId: nullSafeGet('equipmentCatalog.partEquipmentVendorId', lineItem) || undefined,
				images: [],
				supplierFacilityId: lineItem.supplierFacilityId,
				supplierCompanyId: lineItem.supplierCompanyId,
			}))
		);

		return dispatch(createMultipleEpslAsDeletedForSupplier(allEpslEntities));
	}, [dispatch, equipmentLineItems, purchaseRequest]);

	const hideSerialNumberUpdate = useCallback(() => setEquipmentSerialNumberVisible(false), []);

	const onUpdateSerialNumberSuccess = useCallback(() => {
		setCreatedEntities([]);
		setEquipmentSerialNumberVisible(false);
		onSuccess();
	}, [onSuccess]);

	const onReceiptValuesSuccessForPR = useCallback(
		(equipmentReceiptValues) =>
			updatePurchaseRequestStatus(
				prId,
				PURCHASE_REQUEST_STATUSES.fulfilled,
				equipmentReceiptValues
			),
		[prId, updatePurchaseRequestStatus]
	);

	const onOrdered = useCallback(() => {
		setCurrentAction(PR_ACTIONS.ORDER);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.ordered)
			.then(() => onSuccess && onSuccess())
			.catch((err) => message.error(err))
			.finally(() => setCurrentAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const onFulfilled = useCallback(() => {
		setCurrentAction(PR_ACTIONS.FULFILL);

		if (equipmentLineItems.length > 0) {
			const promiseToCall = isLocationPR ? createDeletedAssetsBulk : createDeletedEpslsBulk;
			promiseToCall()
				.then((res) => {
					setCreatedEntities(res);
					setReceivingEntityType(isLocationPR ? 'asset' : 'equipment');
					setEquipmentSerialNumberVisible(true);
				})
				.catch((err) => message.error(err))
				.finally(() => setCurrentAction(null));
		} else {
			updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.fulfilled)
				.then((res) => onSuccess && onSuccess(res))
				.catch((err) => message.error(err))
				.finally(() => setCurrentAction(null));
		}
	}, [
		createDeletedAssetsBulk,
		createDeletedEpslsBulk,
		equipmentLineItems.length,
		isLocationPR,
		onSuccess,
		prId,
		updatePurchaseRequestStatus,
	]);

	return {
		currentAction,
		PR_ACTIONS,
		equipmentLineItems,
		markFulfilledDisabledWithAssetTypeId,
		equipmentSerialNumberVisible,
		createdEntities,
		receivingEntityType,
		onOrdered,
		onFulfilled,
		hideSerialNumberUpdate,
		onUpdateSerialNumberSuccess,
		onReceiptValuesSuccessForPR,
	};
};

export default usePurchaseRequestFulfill;
